import {useMemo, useState} from 'react';

import {oneOfType, node, func} from 'prop-types';

import {API_ENDPOINTS, DRIVN_CONTACT_EMAIL, INVOICE_STATUS, SNACKBAR_ACTIONS, USER_ROLES} from '../const';
import PaymentContext from '../contexts/PaymentContext';
import useHttp from '../hooks/misc/useHttp';
import useReport from '../hooks/providers/useReport';
import useSnackbar from '../hooks/providers/useSnackbar';
import {calculateProductPrice} from '../utils/payment';

// eslint-disable-next-line complexity
const PaymentProvider = ({children}) => {
  const [bills, setBills] = useState(null);
  const [products, setProducts] = useState([]);
  const [getUserBillsErrorMessage, setGetUserBillsErrorMessage] = useState('');
  const [getProductsListErrorMessage, setGetProductsListErrorMessage] = useState('');
  const [updateSubscriptionErrorMessage, setUpdateSubscriptionErrorMessage] = useState('');
  const [authorizationsLoaded, setAuthorizationsLoaded] = useState(false);
  const [authorizationsLoading, setAuthorizationsLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [shouldRefetchAfterAdminModeToggling, setShouldRefetchAfterAdminModeToggling] = useState(false);
  const [isSubmittingSubscriptionForm, setIsSubmittingSubscriptionForm] = useState(false);

  const {reports} = useReport();
  const {showSnackbar, closeSnackbar, defaultSnackbarOptions} = useSnackbar();

  const getDaysUntilDate = frenchFormattedDate => {
    const [day, month, year] = frenchFormattedDate.split('/');
    const endDate = new Date(+year, +month - 1, +day);
    const today = new Date();

    // normalize to midnight
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(diffDays, 0); // never negative
  };

  const userRole = subscriptionData?.profile;
  const hasTrialEnded = userRole === USER_ROLES.OFF_TRIAL;
  const isAdmin = userRole === USER_ROLES.ADMIN;
  const isAdminOff = userRole === USER_ROLES.ADMIN_OFF;
  const isTrial = userRole === USER_ROLES.TRIAL;
  const isGuest = userRole === USER_ROLES.GUEST;
  const isPartner = userRole === USER_ROLES.PARTNER;

  const alreadyUsedReportsDataVolume = reports.reduce((acc, report) => acc + report.size_megaoctets, 0);

  const numberOfProjectsOwnedByUser = subscriptionData?.connections;
  const numberOfProjectsOwnedByAllUsers = subscriptionData?.total_connections;
  const maxNumberOfProjects = subscriptionData?.subscribed_connections;
  const maxReportsDataVolumeInMegaoctets = subscriptionData?.subscribed_megaoctets || 0;
  const reportsDataVolumeInMegaoctetsUsed = subscriptionData?.megaoctets || 0;
  const reportsDataVolumeInMegaoctetsUsedByAllUsers = subscriptionData?.total_megaoctets || 0;
  const numberOfProjectsLeft = (subscriptionData?.subscribed_connections || 0) - (subscriptionData?.connections || 0);
  const numberOfPacksSubscribed = subscriptionData?.subscribed_packs;
  const paidFeaturesDisabled = maxNumberOfProjects < numberOfProjectsOwnedByUser || maxReportsDataVolumeInMegaoctets < reportsDataVolumeInMegaoctetsUsed;
  const endTrialDate = subscriptionData?.end_free_trial;
  const totalNumberOfTrialDays = subscriptionData?.period_trial_days;

  console.log({paidFeaturesDisabled});
  console.log({condition1: maxNumberOfProjects < numberOfProjectsOwnedByUser});
  console.log({condition2: maxReportsDataVolumeInMegaoctets < alreadyUsedReportsDataVolume});

  console.log({maxReportsDataVolumeInMegaoctets, alreadyUsedReportsDataVolume});
  const numberOfTrialDaysLeft = endTrialDate ? getDaysUntilDate(endTrialDate) : null;
  const subscriptions = subscriptionData?.subscriptions || [];
  const hasMembership = !!subscriptions[0]?.subscription_id;

  const subscription = subscriptionData?.subscriptions?.[0];
  const subscriptionContactInfo = subscriptionData?.contact_mail || DRIVN_CONTACT_EMAIL;
  const lastInvoice = subscriptionData?.last_invoice;
  const hasUnpaidInvoice = lastInvoice && lastInvoice.status !== INVOICE_STATUS.PAID;
  const lastInvoiceUrl = lastInvoice?.hosted_invoice_url;

  const productSubscribed = subscription !== null;

  const incrementNumberOfProjects = (numberOfProjectsToAdd = 1) => {
    setSubscriptionData(currentSubcriptionData => {
      return {
        ...currentSubcriptionData,
        connections: (currentSubcriptionData?.connections || 0) + numberOfProjectsToAdd,
        total_connections: (currentSubcriptionData?.total_connections || 0) + numberOfProjectsToAdd
      };
    });
  };

  const decrementNumberOfProjects = () => {
    setSubscriptionData(currentSubcriptionData => {
      return {
        ...currentSubcriptionData,
        connections: currentSubcriptionData.connections - 1,
        total_connections: currentSubcriptionData.total_connections - 1
      };
    });
  };

  const calculateDataVolumeLeft = () => {
    return maxReportsDataVolumeInMegaoctets - alreadyUsedReportsDataVolume;
  };

  const reportsDataVolumeAvailable = calculateDataVolumeLeft();

  const {_post, _get} = useHttp();

  const getUserBills = async () => {
    setGetUserBillsErrorMessage('');
    try {
      const url = API_ENDPOINTS.payment.bills;
      const {response, responseJson: data} = await _get(url);

      if (response.status === 200) {
        const userBills = data;
        if (userBills.length > 0) {
          setBills(userBills);
        }
        return {
          status: 200
        };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setGetUserBillsErrorMessage(e.message);
      return {
        message: e.message
      };
    }
  };

  const toggleAdminMode = async () => {
    try {
      const url = API_ENDPOINTS.payment.toggleAdminMode;
      const snackbarId = showSnackbar(
        SNACKBAR_ACTIONS.TOGGLE_ADMIN_MODE_IN_PROGRESS,
        {
          severity: 'info',
          autoHide: false
        },
        {isDisablingAdminMode: isAdmin}
      );

      const {response} = await _get(url);

      closeSnackbar(snackbarId);

      if (response.status === 200) {
        showSnackbar(SNACKBAR_ACTIONS.TOGGLE_ADMIN_MODE_SUCCESS, defaultSnackbarOptions, {isDisablingAdminMode: isAdmin});
        setShouldRefetchAfterAdminModeToggling(true);

        return {
          status: 200
        };
      }

      return {
        status: response.status
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const getPaymentLink = async baseUrl => {
    try {
      const url = API_ENDPOINTS.payment.create;

      const subscribedProducts = cart
        .filter(item => (item.quantity || 0) > 0)
        .map(p => ({
          price: p.price_id,
          quantity: p.quantity
        }));

      const {response, responseJson: data} = await _post(url, {
        base_url: baseUrl,
        line_items: subscribedProducts
      });

      if (response.status === 200) {
        return {
          status: 200,
          data
        };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      return {
        message: e.message
      };
    }
  };

  const getProductsList = async () => {
    setGetProductsListErrorMessage('');
    setProductsLoading(true);
    try {
      const url = API_ENDPOINTS.payment.products;
      const {response, responseJson: data} = await _get(url);

      if (response.status === 200) {
        setProducts(data);
        return {
          status: 200,
          data
        };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setGetProductsListErrorMessage(e.message);
      return {
        message: e.message
      };
    } finally {
      setProductsLoading(false);
    }
  };

  const setPartnerCustomerId = async () => {
    try {
      const url = API_ENDPOINTS.payment.setPartnerCustomerId;
      const {response} = await _get(url);

      return {
        status: response.status,
        success: response.status === 200
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const updateSubscription = async () => {
    setUpdateSubscriptionErrorMessage('');
    try {
      const url = API_ENDPOINTS.payment.update;

      const alreadySubscribedProducts = cart.filter(cartProduct => subscription.products.find(p => p.product_id === cartProduct.id));
      const formattedSubscribedProducts = alreadySubscribedProducts.map(updatedProduct => ({
        id: subscription.products.find(p => p.product_id === updatedProduct.id).id,
        ...(updatedProduct.quantity === 0 ? {deleted: true} : {}),
        ...(updatedProduct.quantity !== 0 ? {quantity: updatedProduct.quantity} : {})
      }));

      const newProducts = cart.filter(cartProduct => !subscription.products.find(p => p.product_id === cartProduct.id));
      const formattedNewProducts = newProducts.map(p => ({
        quantity: p.quantity,
        price: p.price_id
      }));

      const updatedProducts = [...formattedSubscribedProducts, ...formattedNewProducts];

      const {response, responseJson: data} = await _post(url, {
        subscription_id: subscription.subscription_id,
        items: updatedProducts
      });

      if (response.status === 200) {
        if (data.status !== INVOICE_STATUS.PAID) {
          window.location.href = data.hosted_invoice_url;
          return;
        }

        // eslint-disable-next-line no-use-before-define
        await getUserSubscriptionStatus(true);
        return {
          success: true,
          status: 200,
          data
        };
      }

      return {
        success: false,
        status: response.status
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setUpdateSubscriptionErrorMessage(e.message);
      return {
        success: false,
        message: e.message
      };
    }
  };

  // eslint-disable-next-line complexity,consistent-return
  const getUserSubscriptionStatus = async (shouldForceRefetch = false) => {
    if ((!authorizationsLoaded && !authorizationsLoading) || shouldForceRefetch) {
      if (!shouldForceRefetch) {
        setAuthorizationsLoading(true);
      }
      try {
        const url = API_ENDPOINTS.payment.authorizations;
        const {response, responseJson: data} = await _get(url);

        if (response.status === 200) {
          setAuthorizationsLoaded(true);
          setSubscriptionData(data);
          return {
            status: 200,
            data
          };
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setAuthorizationsLoaded(true);
        return {
          message: e.message
        };
      } finally {
        setAuthorizationsLoading(false);
      }
    }
  };

  const getCartProduct = productId => {
    return cart.find(item => item.id === productId);
  };

  const setCartProductQuantity = (product, newQuantity) => {
    const itemToUpdateIndex = cart.findIndex(item => item.id === product.id);
    if (itemToUpdateIndex !== -1) {
      const updatedCart = [...cart];
      updatedCart[itemToUpdateIndex] = {
        ...updatedCart[itemToUpdateIndex],
        quantity: parseInt(newQuantity, 10)
      };
      setCart(updatedCart);
    } else {
      setCart(currentCart => [...currentCart, {...product, quantity: parseInt(newQuantity, 10)}]);
    }
  };

  const calculateCurrentSubscriptionPrice = () => {
    let total = 0;
    subscription?.products.forEach(prod => {
      const product = products.find(p => p.id === prod.product_id);
      const singleProdPrice = calculateProductPrice(product, prod.quantity);

      total += singleProdPrice;
    });

    return total;
  };

  const isPartnerPolicy = import.meta.env.VITE_COMPANY_POLICY !== 'drivn';
  const hasUserAccessToPaymentFeatures = () => {
    if (isPartnerPolicy) {
      return isAdmin || isAdminOff;
    }
    return !isGuest && !isPartner;
  };

  const hasAccessToPaymentRelatedFeatures = hasUserAccessToPaymentFeatures();

  const useMemoDeps = [
    hasMembership,
    productSubscribed,
    subscription,
    getPaymentLink,
    bills,
    getUserBills,
    getUserBillsErrorMessage,
    products,
    getProductsList,
    getProductsListErrorMessage,
    updateSubscription,
    updateSubscriptionErrorMessage,
    getUserSubscriptionStatus,
    isTrial,
    maxNumberOfProjects,
    numberOfProjectsLeft,
    numberOfTrialDaysLeft,
    endTrialDate,
    numberOfProjectsOwnedByUser,
    numberOfProjectsOwnedByAllUsers,
    isAdmin,
    isAdminOff,
    isGuest,
    isPartner,
    productsLoading,
    authorizationsLoaded,
    cart,
    setCart,
    setCartProductQuantity,
    calculateCurrentSubscriptionPrice,
    maxReportsDataVolumeInMegaoctets,
    reportsDataVolumeAvailable,
    alreadyUsedReportsDataVolume,
    numberOfPacksSubscribed,
    hasTrialEnded,
    totalNumberOfTrialDays,
    paidFeaturesDisabled,
    subscriptionContactInfo,
    reportsDataVolumeInMegaoctetsUsedByAllUsers,
    incrementNumberOfProjects,
    decrementNumberOfProjects,
    toggleAdminMode,
    shouldRefetchAfterAdminModeToggling,
    setShouldRefetchAfterAdminModeToggling,
    hasAccessToPaymentRelatedFeatures,
    hasUnpaidInvoice,
    lastInvoiceUrl,
    isSubmittingSubscriptionForm,
    setIsSubmittingSubscriptionForm
  ];

  const value = useMemo(
    () => ({
      hasMembership,
      productSubscribed,
      subscription,
      getPaymentLink,
      bills,
      getUserBills,
      getUserBillsErrorMessage,
      products,
      getProductsList,
      getProductsListErrorMessage,
      updateSubscription,
      updateSubscriptionErrorMessage,
      getUserSubscriptionStatus,
      isTrial,
      maxNumberOfProjects,
      numberOfProjectsLeft,
      numberOfTrialDaysLeft,
      endTrialDate,
      numberOfProjectsOwnedByUser,
      numberOfProjectsOwnedByAllUsers,
      authorizationsLoading,
      isAdmin,
      isAdminOff,
      isGuest,
      isPartner,
      productsLoading,
      setGetProductsListErrorMessage,
      authorizationsLoaded,
      cart,
      setCart,
      getCartProduct,
      setCartProductQuantity,
      calculateCurrentSubscriptionPrice,
      maxReportsDataVolumeInMegaoctets,
      reportsDataVolumeAvailable,
      alreadyUsedReportsDataVolume,
      numberOfPacksSubscribed,
      hasTrialEnded,
      totalNumberOfTrialDays,
      paidFeaturesDisabled,
      subscriptionContactInfo,
      reportsDataVolumeInMegaoctetsUsedByAllUsers,
      setPartnerCustomerId,
      incrementNumberOfProjects,
      decrementNumberOfProjects,
      toggleAdminMode,
      shouldRefetchAfterAdminModeToggling,
      setShouldRefetchAfterAdminModeToggling,
      hasAccessToPaymentRelatedFeatures,
      hasUnpaidInvoice,
      lastInvoiceUrl,
      isSubmittingSubscriptionForm,
      setIsSubmittingSubscriptionForm
    }),
    useMemoDeps
  );

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};

PaymentProvider.propTypes = {
  children: oneOfType([node, func]).isRequired
};

export default PaymentProvider;
